import { Phone, HeartHandshake, Users, Briefcase, HandHeart, ArrowRight, ShieldCheck } from "lucide-react";
import { MeetingFinder } from "@/components/aa/MeetingFinder";

const AA_BLUE = "#0779bf";
const AA_NAVY = "#03476f";
const HELPLINE = "866-783-7712";
const HELPLINE_HREF = "tel:+18667837712";

const PATHWAYS = [
  {
    icon: HandHeart,
    title: "I want to stop drinking",
    desc: "If drinking has become a problem and you want help, you are why A.A. exists. No forms, no fees, no judgment.",
    cta: "Start here",
    testid: "aa-pathway-newcomer",
  },
  {
    icon: HeartHandshake,
    title: "I'm worried about someone",
    desc: "Watching someone you love struggle is its own kind of pain. Learn what A.A. can do — and where families find support.",
    cta: "For family & friends",
    testid: "aa-pathway-family",
  },
  {
    icon: Briefcase,
    title: "I'm a professional",
    desc: "Doctors, courts, clergy, and employers refer people to A.A. every day. Here is how cooperation works.",
    cta: "For professionals",
    testid: "aa-pathway-professional",
  },
  {
    icon: Users,
    title: "I'm an A.A. member",
    desc: "Service keeps us sober. Find your district, group resources, service opportunities, and area events.",
    cta: "Member resources",
    testid: "aa-pathway-member",
  },
];

export default function AAPilotPage() {
  const scrollToFinder = () => {
    document.getElementById("meeting-finder")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="aa-pilot-page" className="min-h-screen bg-white">
      {/* Pilot concept ribbon */}
      <div data-testid="aa-pilot-ribbon" className="border-b border-black/5 bg-[#eef6fb] px-4 py-1.5 text-center">
        <p className="text-[10.5px] font-extrabold uppercase tracking-[0.14em]" style={{ color: AA_BLUE }}>
          Pilot Concept — CT-AA.org chapter template · live meeting data · Emergent WMM Engine
        </p>
      </div>

      {/* Unmissable helpline bar */}
      <a
        href={HELPLINE_HREF}
        data-testid="aa-helpline-bar"
        className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 px-4 py-2.5 text-white transition-opacity duration-150 hover:opacity-95"
        style={{ background: AA_NAVY }}
      >
        <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#9fd3f2]">
          <Phone size={13} /> 24-Hour Answering Service
        </span>
        <span className="text-[19px] font-extrabold tracking-tight">{HELPLINE}</span>
        <span className="hidden text-[12px] text-white/70 sm:inline">— free & confidential, a member answers day or night</span>
      </a>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(160deg, ${AA_NAVY} 0%, #055e93 70%, ${AA_BLUE} 100%)` }}>
        <div className="mx-auto max-w-5xl px-5 py-16 sm:py-20">
          <p className="text-[12px] font-extrabold uppercase tracking-[0.2em] text-[#9fd3f2]">
            Alcoholics Anonymous · Connecticut (Area 11)
          </p>
          <h1 data-testid="aa-hero-title" className="mt-3 max-w-3xl text-3xl font-extrabold leading-[1.14] text-white sm:text-[44px]">
            If you want to drink, that's your business.{" "}
            <span className="text-[#9fd3f2]">If you want to stop, that's ours.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-white/85">
            A.A. is a fellowship of people who share their experience, strength and hope to solve their
            common problem. Over 1,900 meetings happen across Connecticut every week — one is near you,
            probably today.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="aa-hero-find-meeting-btn"
              onClick={scrollToFinder}
              className="rounded-[10px] bg-white px-7 py-3.5 text-[13px] font-extrabold uppercase tracking-wide shadow-lg transition-transform duration-150 hover:-translate-y-0.5"
              style={{ color: AA_NAVY }}
            >
              Find A Meeting
            </button>
            <a
              href={HELPLINE_HREF}
              data-testid="aa-hero-call-btn"
              className="rounded-[10px] border-2 border-white/50 px-6 py-3 text-[13px] font-extrabold uppercase tracking-wide text-white transition-colors duration-150 hover:border-white"
            >
              Call {HELPLINE}
            </a>
          </div>
        </div>
      </section>

      {/* 4 audience pathways */}
      <section className="bg-[#eef6fb] py-12 sm:py-14">
        <div className="mx-auto max-w-5xl px-5">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.18em]" style={{ color: AA_BLUE }}>How can we help?</p>
          <h2 className="mt-1.5 text-2xl font-extrabold sm:text-3xl" style={{ color: AA_NAVY }}>Four ways in. Choose yours.</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PATHWAYS.map((p) => (
              <div
                key={p.title}
                data-testid={p.testid}
                className="group flex h-full cursor-pointer flex-col rounded-[12px] border border-[#d8e8f3] bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
                style={{ borderTop: `4px solid ${AA_BLUE}` }}
              >
                <p.icon size={26} style={{ color: AA_BLUE }} />
                <h3 className="mt-3 text-[16.5px] font-extrabold leading-snug" style={{ color: AA_NAVY }}>{p.title}</h3>
                <p className="mt-2 flex-1 text-[13.5px] leading-relaxed text-[#54697a]">{p.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-extrabold uppercase tracking-wide transition-transform duration-150 group-hover:translate-x-0.5" style={{ color: AA_BLUE }}>
                  {p.cta} <ArrowRight size={13} />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live meeting finder */}
      <section id="meeting-finder" className="scroll-mt-24 py-12 sm:py-14">
        <div className="mx-auto max-w-5xl px-5">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.18em]" style={{ color: AA_BLUE }}>Live Meeting Finder</p>
          <h2 className="mt-1.5 text-2xl font-extrabold sm:text-3xl" style={{ color: AA_NAVY }}>
            1,900+ meetings a week. Filter down to yours.
          </h2>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-[#54697a]">
            Search every A.A. meeting in Connecticut by town, day, format, and attendance — powered live by
            the Area 11 meeting registry, the same data behind the Meeting Guide app.
          </p>
          <div className="mt-6">
            <MeetingFinder />
          </div>
        </div>
      </section>

      {/* Responsibility statement + anonymity */}
      <section className="py-12 text-white sm:py-14" style={{ background: AA_NAVY }}>
        <div className="mx-auto grid max-w-5xl gap-8 px-5 sm:grid-cols-2">
          <div>
            <ShieldCheck size={26} className="text-[#9fd3f2]" />
            <h2 className="mt-3 text-xl font-extrabold sm:text-2xl">Anonymity is our foundation</h2>
            <p className="mt-2 text-[14.5px] leading-relaxed text-white/80">
              Who you see and what you hear at a meeting stays there. You never have to give your full name —
              this template uses no member photographs, consistent with A.A. tradition at the public level.
            </p>
          </div>
          <div className="rounded-[12px] border border-white/15 bg-white/[0.06] p-6">
            <p className="text-[15.5px] italic leading-relaxed text-white/90">
              "I am responsible. When anyone, anywhere, reaches out for help, I want the hand of A.A. always
              to be there. And for that: I am responsible."
            </p>
            <p className="mt-3 text-[12px] font-extrabold uppercase tracking-[0.14em] text-[#9fd3f2]">The Responsibility Declaration</p>
          </div>
        </div>
      </section>

      {/* Footer helpline repeat */}
      <footer className="border-t border-black/5 bg-[#eef6fb] py-8">
        <div className="mx-auto max-w-5xl px-5 text-center">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#54697a]">Feeling like you need to talk to someone right now?</p>
          <a href={HELPLINE_HREF} data-testid="aa-footer-helpline" className="mt-1 inline-block text-2xl font-extrabold transition-opacity duration-150 hover:opacity-75" style={{ color: AA_BLUE }}>
            {HELPLINE}
          </a>
          <p className="mt-1 text-[12px] text-[#8fb4cd]">24 hours a day · free and confidential · Connecticut Area 11</p>
        </div>
      </footer>
    </div>
  );
}
